import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { getUserProfile } from './api/api'
import { env } from './env'
import { classifyPortalMembership, type PortalMembership } from './portal-membership'
import { useInactivityTimeout } from '../hooks/use-inactivity-timeout'
import { toast } from 'sonner'
import { InactivityWarningToast } from '../components/inactivity-warning-toast'

/**
 * FF-3636. Derived from the session's assurance level, unlike `isTOTPVerified`, which is reset on
 * every auth state change and so is always false on a restored session.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'mfa_required'

interface AuthContextType {
  authStatus: AuthStatus
  /** Set when this portal rejected the user. Null normally. */
  wrongPortal: PortalMembership | null
  /** True while the rejection is redirecting the user to their own portal (destination configured). */
  wrongPortalRedirecting: boolean
  isAuthenticated: boolean
  user: User | null
  session: Session | null
  isBanned: boolean
  bannedUntil: string | null
  isTOTPVerified: boolean
  setIsTOTPVerified: (verified: boolean) => void
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string) => Promise<{ session: Session | null, user: User | null }>
  logout: () => Promise<void>
  refreshSession: () => Promise<void>
  getToken: () => string | null
  checkBanStatus: () => Promise<void>
  // Configuration
  inactivityTimeoutMs: number
  warningTimeMs: number
}

interface AuthProviderProps {
  children: React.ReactNode
  // Optional configuration for testing
  inactivityTimeoutMs?: number // Default: 30 minutes
  warningTimeMs?: number // Default: 1 minute
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children, inactivityTimeoutMs = 30 * 60 * 1000, warningTimeMs = 60 * 1000 }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isBanned, setIsBanned] = useState(false)
  const [bannedUntil, setBannedUntil] = useState<string | null>(null)
  const [isTOTPVerified, setIsTOTPVerified] = useState(false)
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading')
  const [wrongPortal, setWrongPortal] = useState<PortalMembership | null>(null)
  const [wrongPortalRedirecting, setWrongPortalRedirecting] = useState(false)
  const rejectingRef = useRef(false)
  const warningToastIdRef = useRef<string | number | null>(null)

  // Configurable timeouts - defaults to 30 minutes and 1 minute warning
  const INACTIVITY_TIMEOUT = inactivityTimeoutMs
  const WARNING_TIME = warningTimeMs

  console.log('[AuthProvider] Configured timeouts:', {
    inactivityTimeoutMs: INACTIVITY_TIMEOUT,
    warningTimeMs: WARNING_TIME,
    inactivityTimeoutMinutes: INACTIVITY_TIMEOUT / (60 * 1000),
    warningTimeSeconds: WARNING_TIME / 1000
  })

  const logout = async () => {
    try {
      console.log('[AuthContext] Starting logout process')

      // Dismiss any warning toast
      if (warningToastIdRef.current) {
        toast.dismiss(warningToastIdRef.current)
        warningToastIdRef.current = null
      }

      // Clear Supabase session
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.warn('Error during signOut:', error.message)
      }

      // Always clear local state regardless of API errors
      setIsAuthenticated(false)
      setUser(null)
      setSession(null)
      setIsTOTPVerified(false)

      // Clear any remaining authentication-related data from localStorage
      try {
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && (key.startsWith('incept5-auth') || key.startsWith('supabase'))) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach(key => {
          console.log('[AuthContext] Removing localStorage key:', key)
          localStorage.removeItem(key)
        })
      } catch (storageError) {
        console.warn('Error clearing localStorage:', storageError)
      }

      // Clear sessionStorage as well
      try {
        const sessionKeysToRemove = []
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i)
          if (key && (key.startsWith('incept5-auth') || key.startsWith('supabase'))) {
            sessionKeysToRemove.push(key)
          }
        }
        sessionKeysToRemove.forEach(key => {
          console.log('[AuthContext] Removing sessionStorage key:', key)
          sessionStorage.removeItem(key)
        })
      } catch (storageError) {
        console.warn('Error clearing sessionStorage:', storageError)
      }

      console.log('[AuthContext] Logout process completed')
    } catch (error) {
      console.error('Exception during logout:', error)
      // Still clear local state even if there's an exception
      setIsAuthenticated(false)
      setUser(null)
      setSession(null)
      setIsTOTPVerified(false)
    }
  }

  // Handle inactivity timeout warning
  const handleInactivityWarning = (secondsRemaining: number) => {
    console.log('[AuthContext] Session warning:', secondsRemaining, 'seconds remaining')

    // Only create the toast once when the warning period starts
    if (!warningToastIdRef.current) {
      const handleStayLoggedIn = () => {
        // User activity will automatically reset the timeout
        // Just dismiss the toast
        if (warningToastIdRef.current) {
          toast.dismiss(warningToastIdRef.current)
          warningToastIdRef.current = null
        }
      }

      warningToastIdRef.current = toast(
        <InactivityWarningToast
          initialSeconds={secondsRemaining}
          onStayLoggedIn={handleStayLoggedIn}
        />,
        {
          duration: Infinity, // Keep toast open until manually dismissed
          className: 'warning-toast'
        }
      )
    }
    // Don't do anything for subsequent calls - let the component handle the countdown
  }

  // Handle inactivity timeout
  const handleInactivityTimeout = async () => {
    console.log('[AuthContext] Session expired due to inactivity, logging out')

    // Dismiss warning toast if it's still showing
    if (warningToastIdRef.current) {
      toast.dismiss(warningToastIdRef.current)
      warningToastIdRef.current = null
    }

    await logout()

    // Force redirect to login page after logout
    window.location.hash = '#/auth/login'
  }

  // Create a custom activity handler that clears warning toast
  const handleActivity = useCallback(() => {
    // Clear any existing warning toast when activity is detected
    if (warningToastIdRef.current) {
      console.log('[AuthContext] User activity detected, dismissing warning toast')
      toast.dismiss(warningToastIdRef.current)
      warningToastIdRef.current = null
    }
  }, [])

  // Use inactivity timeout hook - active when user is authenticated (regardless of TOTP status)
  // This ensures that even if TOTP verification gets stuck, the user will still be logged out
  const { resetTimeout } = useInactivityTimeout({
    timeout: INACTIVITY_TIMEOUT,
    onTimeout: handleInactivityTimeout,
    onWarning: handleInactivityWarning,
    warningTime: WARNING_TIME,
    isActive: isAuthenticated
  })

  // Override resetTimeout to include toast cleanup
  useEffect(() => {
    if (!isAuthenticated) return

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ]

    const activityHandler = () => {
      handleActivity()
      resetTimeout()
    }

    events.forEach(event => {
      document.addEventListener(event, activityHandler, { passive: true })
    })

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, activityHandler)
      })
    }
  }, [isAuthenticated, handleActivity, resetTimeout])

  /**
   * FF-3636. Sign-out precedes the redirect and is never conditional on having a destination: a
   * persisted session auto-restores and walks past the check again. 'unknown' has no home portal.
   */
  const rejectWrongPortal = useCallback(async (membership: PortalMembership) => {
    if (rejectingRef.current) return
    rejectingRef.current = true

    const destination =
      membership === 'partner'
        ? env.VITE_USER_DASHBOARD_URL
        : membership === 'merchant'
        ? env.VITE_MERCHANT_DASHBOARD_URL
        : undefined

    console.log('[AuthContext] Wrong portal for this user, signing out', { membership, destination })
    await supabase.auth.signOut()
    setIsAuthenticated(false)
    setUser(null)
    setSession(null)
    setAuthStatus('unauthenticated')
    setWrongPortal(membership)
    setWrongPortalRedirecting(!!destination)

    if (destination) {
      window.location.replace(destination)
    }
  }, [])

  /**
   * FF-3636. The single post-authentication gate, on initial getSession() and every auth state
   * change, so a restored session is checked like a fresh sign-in.
   */
  const loadProfile = useCallback(
    async (currentSession: Session | null) => {
      if (!currentSession) {
        setAuthStatus('unauthenticated')
        return
      }

      // totp-verify navigates to /admin the moment setSession resolves, while this still awaits the
      // profile. Hold at 'loading': a stale 'mfa_required' bounces a just-verified admin to login.
      rejectingRef.current = false
      setWrongPortal(null)
      setWrongPortalRedirecting(false)
      setAuthStatus((current) => (current === 'authenticated' ? current : 'loading'))

      try {
        const { data: mfaData, error: mfaError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
        if (mfaError) throw mfaError
        if ((mfaData?.currentLevel ?? 'aal1') !== 'aal2') {
          setAuthStatus('mfa_required')
          return
        }

        const userProfile = await getUserProfile()

        const membership = classifyPortalMembership(userProfile)
        if (membership !== 'backoffice') {
          await rejectWrongPortal(membership)
          return
        }

        const isBannedNow = !!userProfile.bannedUntil && new Date(userProfile.bannedUntil) > new Date()
        setIsBanned(isBannedNow)
        setBannedUntil(userProfile.bannedUntil)
        if (isBannedNow) {
          console.log('[AuthContext] User is banned, logging out')
          await logout()
          return
        }

        setAuthStatus('authenticated')
      } catch (error) {
        // Fail closed on first load only; a later transient error must not evict a working session.
        console.error('[AuthContext] Error loading profile:', error)
        setAuthStatus((current) => (current === 'loading' ? 'unauthenticated' : current))
      }
    },
    [rejectWrongPortal],
  )

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session)
      setUser(session?.user ?? null)
      setSession(session)
      // Reset TOTP verification on new session
      setIsTOTPVerified(false)
      // No loadProfile here: onAuthStateChange fires INITIAL_SESSION with this same session, and
      // gating from both would fetch the profile twice on every load.
    })

    // Listen for changes on auth state (logged in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        console.log('[AuthContext] Auth state change:', event)
        setIsAuthenticated(!!session)
        setUser(session?.user ?? null)
        setSession(session)
        void loadProfile(session)

        // Handle logout/signed out events
        if (event === 'SIGNED_OUT' && !session) {
          console.log('[AuthContext] User signed out, clearing state and redirecting')
          setIsAuthenticated(false)
          setUser(null)
          setSession(null)
          setIsTOTPVerified(false)

          // FF-3636: a rejection in flight owns the navigation; bouncing to login would race it.
          if (
            !rejectingRef.current &&
            window.location.hash !== '#/auth/login' &&
            !window.location.hash.startsWith('#/auth/')
          ) {
            console.log('[AuthContext] Redirecting to login page after logout')
            window.location.hash = '#/auth/login'
          }
        }

        // Always reset TOTP verification on any auth state change except manual verification
        if (event === 'SIGNED_OUT' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Resetting TOTP verification due to auth state change:', event)
          setIsTOTPVerified(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [loadProfile])

  const refreshSession = async () => {
    const { data: { session }, error } = await supabase.auth.refreshSession()
    if (error) throw error
    setSession(session)
    setUser(session?.user ?? null)
    setIsAuthenticated(!!session)
  }

  const getToken = (): string | null => {
    return session?.access_token ?? null
  }

  const login = async (email: string, password: string) => {
    console.log('[AuthContext] Starting login process')
    // Always reset TOTP verification on new login attempts
    setIsTOTPVerified(false)

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error

    // Explicitly set the session to ensure it's available for MFA operations
    if (data.session) {
      console.log('[AuthContext] Setting session after password authentication')
      setSession(data.session)
      setUser(data.user)
      setIsAuthenticated(true)
    }

    console.log('[AuthContext] Password authentication successful, TOTP verification required')
  }

  const signup = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    if (error) throw error
    return { session: data.session, user: data.user }
  }

  /** Kept for the axios interceptor's USER_BANNED re-check; delegates to the single gate. */
  const checkBanStatus = async () => {
    await loadProfile(session)
  }

  return (
    <AuthContext.Provider value={{
        authStatus,
        wrongPortal,
        wrongPortalRedirecting,
      isAuthenticated,
      user,
      session,
      isBanned,
      bannedUntil,
      isTOTPVerified,
      setIsTOTPVerified,
      login,
      signup,
      logout,
      refreshSession,
      getToken,
      checkBanStatus,
      inactivityTimeoutMs: INACTIVITY_TIMEOUT,
      warningTimeMs: WARNING_TIME
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
